import { Clock } from "../domain/Clock";
import { ClockRepo, SaveClockOptions, RequestNewClockData } from "../domain/ClockRepo";
import { sync_command_dispatcher, query_dispatcher } from "@/api/client";
import { User } from "../domain/User";
import { v4 as uuidv4 } from 'uuid';

export class APIClockRepository implements ClockRepo {
    command = 'update_clock';
    async save(clock: Clock, options?: SaveClockOptions): Promise<void> {
        const info: any = {
            uuid: clock.uuid,
            timestamp: clock.timestamp,
            type: clock.type,
            user: clock.user,
            justification: clock.changes[clock.changes.length-1].justification,
            location: clock.location
        };

        if (options?.send_email_notification) {
            info.send_email_notification = true;
        }
        if (options?.companydb_id) {
            info.companydb_id = options.companydb_id;
        }
        info.is_admin_or_owner = options?.is_admin_or_owner ?? false;

        return await sync_command_dispatcher.dispatch(this.command, info);
    }

    async find_all(): Promise<Clock[]> {
        const result = await query_dispatcher.dispatch('FindUserClocksQuery', {});

        return result.map((item: any) => {
            return new Clock(item.uuid, new Date(item.timestamp), item.type, item.user, item.changes, item.sendEmailNotification || false, item.location || 'office');
        });
    }

    async find_last(): Promise<Clock | undefined>{
        const response = await query_dispatcher.dispatch("last.clock", {});

        if (!response || !response.uuid || !response.type) {
            return undefined;
        }

        return new Clock(
            response.uuid,
            new Date(response.timestamp),
            response.type,
            response.user,
            response.changes || [],
            response.sendEmailNotification || false,
            response.location || 'office'
        );
    }

    async create(clockData: {
        user_id: number;
        timestamp: Date;
        type: string;
    }): Promise<any> {
        const command = {
            target_user_id: clockData.user_id,
            timestamp: clockData.timestamp.toISOString(),
            uuid: uuidv4(),
            force_create: true
        };

        const commandMap: Record<string, string> = {
            'in': 'clock_in',
            'out': 'clock_out',
            'pause': 'clock_pause',
            'continue': 'clock_continue'
        };
        const commandName = commandMap[clockData.type] || 'clock_in';
        return await sync_command_dispatcher.dispatch(commandName, command);
    }

    async approve_change_modification(clock_uuid: string, change_index: number, admin_user_id: number, new_status: string): Promise<void> {
        const commandData = {
            clock_uuid: clock_uuid,
            change_index: change_index,
            admin_user_id: admin_user_id,
            new_status: new_status
        };

        return await sync_command_dispatcher.dispatch('approve_clock_modification', commandData);
    }

    async requestNew(data: RequestNewClockData): Promise<{ success: boolean; uuid: string; message: string }> {
        const commandData = {
            user_id: data.user_id,
            timestamp: data.timestamp.toISOString(),
            type: data.type,
            justification: data.justification,
            uuid: uuidv4(),
            location: data.location || 'office',
            send_email_notification: data.send_email_notification || false,
            companydb_id: data.companydb_id
        };

        return await sync_command_dispatcher.dispatch('request_new_clock', commandData);
    }

    async deletePauseClock(clock_uuid: string): Promise<void> {
        return await sync_command_dispatcher.dispatch('delete_pause_clock', { clock_uuid });
    }

    async find_by_criteria(criteria: any[][], order_by?: string[], limit?: number, offset?: number, powerOrNot?: boolean): Promise<Clock[]> {
        const payload = {
            filters: criteria,  // Los filtros incluyen ['user_id', '==', selectedUserId]
            limit,
            offset,
            order_by: order_by || ['-timestamp'],
            powerOrNot: powerOrNot || false
        };
        const response = await query_dispatcher.dispatch('filter_clocks.query', payload);

        const clocks = response.map((item: any) => {
            return new Clock(
                item.uuid,           // uuid
                new Date(item.timestamp), // timestamp
                item.type,           // type
                item.user,           // user object
                item.changes || [],   // changes
                item.sendEmailNotification || false, // sendEmailNotification
                item.location || 'office' // location
            );
        });

        return clocks;
    }
}

export { APIClockRepository as APIClockRepo };

export class APIClockInRepository extends APIClockRepository {
    command = 'clock_in';
}

export class APIClockOutRepository extends APIClockRepository {
    command = 'clock_out';
}

export class APIClockPauseRepository extends APIClockRepository {
    command = 'clock_pause';
}

export class APIClockContinueRepository extends APIClockRepository {
    command = 'clock_continue';
}

export class APIApproveClockModificationCommand {
    async execute(clock_uuid: string, change_index: number, admin_user_id: string, new_status: string): Promise<void> {
        const commandData = {
            clock_uuid: clock_uuid,
            change_index: change_index,
            admin_user_id: parseInt(admin_user_id),
            new_status: new_status
        };

        return await sync_command_dispatcher.dispatch('approve_clock_modification', commandData);
    }
}

export class APIUpdateClockLocationCommand {
    async execute(user_id: number, location: string): Promise<void> {
        const commandData = {
            user_id: user_id,
            location: location
        };

        return await sync_command_dispatcher.dispatch('update_clock_location', commandData);
    }
}